import type { TodayApproval } from './todayData';

/**
 * Today → Went out: one row per page, article, form or the site, however
 * many times it was approved in the window. The row says which version is
 * live and how many times it went out; the versions are behind History.
 */
export interface WentOutGroup {
  key: string;
  /** Newest first. */
  events: TodayApproval[];
  latest: TodayApproval;
  /** The publish that is live now, if one in the window still is. */
  live: TodayApproval | null;
  publishes: number;
}

export function groupApprovals(approvals: TodayApproval[]): WentOutGroup[] {
  const byKey = new Map<string, TodayApproval[]>();
  for (const a of approvals) {
    const key = `${a.kind}:${a.subjectId}`;
    byKey.set(key, [...(byKey.get(key) || []), a]);
  }
  return [...byKey].map(([key, list]) => {
    const events = [...list].sort(
      (a, b) => new Date(b.recordedAt).getTime() - new Date(a.recordedAt).getTime()
    );
    return {
      key,
      events,
      latest: events[0],
      live: events.find((e) => e.action === 'publish' && e.stillLive) || null,
      publishes: events.filter((e) => e.action === 'publish').length,
    };
  });
}

const version = (a: TodayApproval) => (a.versionNumber ? `v${a.versionNumber}` : null);

/** "v17 live · published 6× today", for a group of more than one approval. */
export function groupSummary(g: WentOutGroup, period: string) {
  const live = g.live ? version(g.live) : null;
  const count =
    g.publishes === g.events.length
      ? `published ${g.publishes}× ${period}`
      : `${g.events.length} approvals ${period}`;
  return [live ? `${live} live` : null, count].filter(Boolean).join(' · ');
}

const VERB: Record<string, string> = {
  publish: 'Published',
  schedule: 'Scheduled',
  unpublish: 'Took down',
};

/** One line of a group's History: when, which version, what, and whether it's live. */
export function historyLine(a: TodayApproval) {
  return {
    id: a.id,
    at: new Date(a.recordedAt),
    version: version(a) || '—',
    what: [VERB[a.action] || a.action, a.approvedBy ? `by ${a.approvedBy}` : null]
      .filter(Boolean)
      .join(' '),
    status:
      a.action !== 'publish' ? null : a.stillLive ? ('Live' as const) : ('No longer live' as const),
  };
}
